import Loader from 'components/Loader';
import useModelSpecifications from 'api/model-specifications/useModelSpecifications';
import { useMemo, useState } from 'react';
import { useKeyboard } from 'hooks/useKeyboard';
import { useSearchParams } from 'react-router-dom';
import { LLMModelSpecification, MODEL_TYPES, ModelType } from 'api/model';
import ModelSpecificationAdd from './ModelSpecificationAdd';
import ModelSpecificationEdit from './ModelSpecificationEdit';
import ModelProviderCard from './ModelProviderCard';
import ModelSpecificationCard from './ModelSpecificationCard';
import ConfirmationDialog from 'components/dialog/ConfirmationDialog';
import useDialog from 'hooks/useDialog';
import { Box, Button, Typography } from '@mui/material';
import { Add, ArrowBack, DeleteOutline, DragIndicator, Refresh, Save } from '@mui/icons-material';
import { ActionIconButton } from 'components/button/ActionIconButton';
import { SettingsDetailPanel } from 'components/settingsHub/SettingsDetailPanel';
import { EntityGrid } from 'components/settingsHub/settingsEntities.styles';
import { DragDropContext, Draggable, DropResult, Droppable } from '@hello-pangea/dnd';

const SPEC_CARD_WIDTH = 300;
const SPEC_CARD_MIN_TILE = 280;

type ModelSpecificationGroup = {
  group: string;
  models: LLMModelSpecification[];
};

type ModeActionsProps = {
  isReordering: boolean;
  isDirty: boolean;
  isSaving: boolean;
  onStartReorder: () => void;
  onCancelReorder: () => void;
  onSaveReorder: () => void;
};

const ModeActions = ({
  isReordering,
  isDirty,
  isSaving,
  onStartReorder,
  onCancelReorder,
  onSaveReorder,
}: ModeActionsProps) => (
  <Box className="flex items-center justify-end">
    {isReordering ? (
      <Box className="flex items-center gap-2">
        <Button variant="outlined" disabled={isSaving} onClick={onCancelReorder}>
          Cancel
        </Button>
        <Button variant="contained" startIcon={<Save />} disabled={!isDirty || isSaving} onClick={onSaveReorder}>
          {isSaving ? 'Saving...' : 'Save Order'}
        </Button>
      </Box>
    ) : (
      <Button variant="contained" startIcon={<DragIndicator />} onClick={onStartReorder}>
        Reorder
      </Button>
    )}
  </Box>
);

type ReorderModeContentProps = {
  groups: ModelSpecificationGroup[];
  onDragEnd: (result: DropResult) => void;
};

const ReorderModeContent = ({ groups, onDragEnd }: ReorderModeContentProps) => (
  <DragDropContext onDragEnd={onDragEnd}>
    <Droppable droppableId="groups" type="GROUP">
      {(groupDropProvided) => (
        <div ref={groupDropProvided.innerRef} {...groupDropProvided.droppableProps} className="flex flex-col gap-4">
          {groups.map(({ group, models }, groupIndex) => (
            <Draggable key={`group:${group}`} draggableId={`group:${group}`} index={groupIndex}>
              {(groupDragProvided) => (
                <Box
                  ref={groupDragProvided.innerRef}
                  {...groupDragProvided.draggableProps}
                  {...groupDragProvided.dragHandleProps}
                  className="rounded-lg border border-solid p-4 shadow-sm"
                  sx={{
                    borderColor: 'divider',
                    backgroundColor: 'background.paper',
                    ...groupDragProvided.draggableProps.style,
                  }}
                >
                  <Box className="mb-3 flex items-center justify-between cursor-grab active:cursor-grabbing">
                    <Typography variant="subtitle2" color="text.primary" className="font-semibold">
                      {group}
                    </Typography>
                    <Box
                      className="flex items-center rounded px-1"
                      sx={{
                        color: 'text.secondary',
                        '&:hover': { color: 'text.primary' },
                      }}
                      title="Drag to reorder group"
                    >
                      <DragIndicator fontSize="small" />
                    </Box>
                  </Box>
                  <Droppable droppableId={`models:${group}`} type="MODEL" direction="horizontal">
                    {(modelDropProvided) => (
                      <Box
                        ref={modelDropProvided.innerRef}
                        {...modelDropProvided.droppableProps}
                        className="flex items-start gap-3 overflow-x-auto py-1"
                      >
                        {models.map((model, modelIndex) => (
                          <Draggable key={`model:${model.name}`} draggableId={`model:${model.name}`} index={modelIndex}>
                            {(modelDragProvided) => (
                              <Box
                                ref={modelDragProvided.innerRef}
                                {...modelDragProvided.draggableProps}
                                {...modelDragProvided.dragHandleProps}
                                className="shrink-0"
                                sx={{ width: SPEC_CARD_WIDTH, ...modelDragProvided.draggableProps.style }}
                              >
                                <Box
                                  className="relative cursor-grab active:cursor-grabbing"
                                  title="Drag to reorder model"
                                >
                                  <Box
                                    className="absolute right-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded"
                                    sx={{ color: 'text.secondary' }}
                                  >
                                    <DragIndicator fontSize="small" />
                                  </Box>
                                  <ModelSpecificationCard model={model} />
                                </Box>
                              </Box>
                            )}
                          </Draggable>
                        ))}
                        {modelDropProvided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </Box>
              )}
            </Draggable>
          ))}
          {groupDropProvided.placeholder}
        </div>
      )}
    </Droppable>
  </DragDropContext>
);

type DisplayModeContentProps = {
  groups: ModelSpecificationGroup[];
  isDeleting: boolean;
  onDelete: (event: React.MouseEvent, model: LLMModelSpecification) => void;
  onEdit: (model: LLMModelSpecification) => void;
};

const DisplayModeContent = ({ groups, isDeleting, onDelete, onEdit }: DisplayModeContentProps) => (
  <Box className="flex flex-col gap-4">
    {groups.map(({ group, models }) => (
      <Box key={group}>
        <Typography variant="subtitle2" color="text.secondary" className="mb-2">
          {group}
        </Typography>
        <EntityGrid minTile={SPEC_CARD_MIN_TILE}>
          {models.map((model) => (
            <Box
              key={`${model.provider}-${model.name}`}
              className="group relative cursor-pointer"
              onClick={() => onEdit(model)}
            >
              <ModelSpecificationCard
                model={model}
                action={
                  <ActionIconButton
                    size="small"
                    color="error"
                    disabled={isDeleting}
                    onClick={(e) => onDelete(e, model)}
                    className="!absolute right-2 top-2 z-10 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                    title="Delete model specification"
                  >
                    <DeleteOutline fontSize="small" />
                  </ActionIconButton>
                }
              />
            </Box>
          ))}
        </EntityGrid>
      </Box>
    ))}
  </Box>
);

type Props = {
  onClose?: () => void;
};

const ModelSpecificationsView = ({ onClose }: Props) => {
  const { refresh } = useModelSpecifications(undefined, false);

  const [addModelFormOpen, setAddModelFormOpen] = useState(false);
  const [editModelSpecification, setEditModelSpecification] = useState<LLMModelSpecification | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedProvider, setSelectedProvider] = useState<ModelType | null>(null);

  const refreshModels = async () => {
    await refresh.mutateAsync();
  };

  useKeyboard({
    keyDown: (e) => {
      if (e.key === 'Escape' && searchParams.get('config')) {
        if (editModelSpecification || addModelFormOpen) {
          setEditModelSpecification(null);
          setAddModelFormOpen(false);
        } else if (onClose) {
          onClose();
        } else {
          setSearchParams({});
        }
      }
    },
  });

  const handleToggleProvider = (provider: ModelType) => {
    setSelectedProvider((prev) => (prev === provider ? null : provider));
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setSearchParams({});
    }
  };

  return (
    <>
      <div className="flex w-full flex-col gap-6">
        <Box className="flex items-center justify-end gap-4">
          <Box className="flex items-center gap-2">
            {onClose && (
              <Button variant="outlined" onClick={handleClose} startIcon={<ArrowBack />}>
                Back to Models
              </Button>
            )}
            <Button variant="contained" onClick={() => setAddModelFormOpen(true)} startIcon={<Add />}>
              Add Specification
            </Button>
            <Button variant="contained" onClick={() => void refreshModels()} startIcon={<Refresh />}>
              Refresh Specifications
            </Button>
          </Box>
        </Box>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          {MODEL_TYPES.map((provider) => (
            <div key={provider} className="flex flex-col gap-3">
              <div className="flex flex-col items-center gap-2">
                <ModelProviderCard
                  provider={provider}
                  selected={selectedProvider === provider}
                  onClick={() => handleToggleProvider(provider)}
                />
                <span className="text-xs uppercase tracking-wide text-gray-500">{provider}</span>
              </div>
            </div>
          ))}
        </div>
        <div>
          {selectedProvider && (
            <ProviderModelSpecifications
              key={selectedProvider}
              provider={selectedProvider}
              onEdit={(modelSpecification) => setEditModelSpecification(modelSpecification)}
            />
          )}
        </div>
      </div>

      {addModelFormOpen && (
        <SettingsDetailPanel onClose={() => setAddModelFormOpen(false)}>
          <ModelSpecificationAdd onClose={() => setAddModelFormOpen(false)} />
        </SettingsDetailPanel>
      )}

      {editModelSpecification && (
        <SettingsDetailPanel onClose={() => setEditModelSpecification(null)}>
          <ModelSpecificationEdit
            modelSpecification={editModelSpecification}
            onClose={() => setEditModelSpecification(null)}
          />
        </SettingsDetailPanel>
      )}
    </>
  );
};

const ProviderModelSpecifications = ({
  provider,
  onEdit,
}: {
  provider: ModelType;
  onEdit: (modelSpecification: LLMModelSpecification) => void;
}) => {
  const { modelSpecifications, isLoading, reorder, remove } = useModelSpecifications(provider);
  const [showDeleteDialog, DeleteDialog] = useDialog(ConfirmationDialog);
  const [modelToDelete, setModelToDelete] = useState<LLMModelSpecification | null>(null);

  const sortedModelSpecifications = useMemo(() => {
    return [...(modelSpecifications ?? [])].sort((a, b) => {
      if (a.group_order === b.group_order) {
        return a.model_order - b.model_order;
      }
      return a.group_order - b.group_order;
    });
  }, [modelSpecifications]);

  const grouped = useMemo<ModelSpecificationGroup[]>(() => {
    const groupedMap = (sortedModelSpecifications ?? []).reduce<Record<string, LLMModelSpecification[]>>(
      (acc, model) => {
        const groupName = model.group;
        if (!acc[groupName]) {
          acc[groupName] = [];
        }
        acc[groupName].push(model);
        return acc;
      },
      {},
    );

    return Object.entries(groupedMap).map(([group, models]) => ({ group, models }));
  }, [sortedModelSpecifications]);

  const [orderedGroups, setOrderedGroups] = useState<ModelSpecificationGroup[] | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  const displayedGroups = orderedGroups ?? grouped;

  const getGroupNameFromDroppable = (droppableId: string): string => {
    const prefix = 'models:';
    return droppableId.startsWith(prefix) ? droppableId.slice(prefix.length) : droppableId;
  };

  const handleGroupDragEnd = ({ source, destination }: DropResult) => {
    if (!destination) {
      return;
    }

    if (source.index === destination.index) {
      return;
    }

    const reorderedGroups = [...displayedGroups];
    const [removed] = reorderedGroups.splice(source.index, 1);
    reorderedGroups.splice(destination.index, 0, removed);
    setOrderedGroups(reorderedGroups);
    setIsDirty(true);
  };

  const handleModelDragEnd = ({ source, destination }: DropResult) => {
    if (!destination) {
      return;
    }

    const sourceGroupName = getGroupNameFromDroppable(source.droppableId);
    const destinationGroupName = getGroupNameFromDroppable(destination.droppableId);

    if (source.droppableId === destination.droppableId && source.index === destination.index) {
      return;
    }

    const sourceGroupIndex = displayedGroups.findIndex(({ group }) => group === sourceGroupName);
    if (sourceGroupIndex === -1) {
      return;
    }

    const destinationGroupIndex = displayedGroups.findIndex(({ group }) => group === destinationGroupName);
    if (destinationGroupIndex === -1) {
      return;
    }

    const sourceGroup = displayedGroups[sourceGroupIndex];
    const destinationGroup = displayedGroups[destinationGroupIndex];
    const sourceModels = [...sourceGroup.models];
    const destinationModels = sourceGroupIndex === destinationGroupIndex ? sourceModels : [...destinationGroup.models];
    const [movedModel] = sourceModels.splice(source.index, 1);

    if (!movedModel) {
      return;
    }

    destinationModels.splice(destination.index, 0, movedModel);

    const newOrderedGroups = [...displayedGroups];
    newOrderedGroups[sourceGroupIndex] = { ...sourceGroup, models: sourceModels };
    newOrderedGroups[destinationGroupIndex] = { ...destinationGroup, models: destinationModels };

    setOrderedGroups(newOrderedGroups);
    setIsDirty(true);
  };

  const handleDragEnd = (dropResult: DropResult) => {
    switch (dropResult.type) {
      case 'GROUP':
        handleGroupDragEnd(dropResult);
        break;
      case 'MODEL':
        handleModelDragEnd(dropResult);
        break;
      default:
        break;
    }
  };

  const handleSaveReorder = async () => {
    if (!orderedGroups) {
      return;
    }

    await reorder.mutateAsync({
      provider,
      payload: {
        groups: displayedGroups.map(({ group, models }) => ({
          group,
          models: models.map((model) => model.name),
        })),
      },
    });
    setIsDirty(false);
    setOrderedGroups(null);
    setIsReordering(false);
  };

  const handleStartReorder = () => {
    setOrderedGroups(grouped);
    setIsDirty(false);
    setIsReordering(true);
  };

  const handleCancelReorder = () => {
    setOrderedGroups(null);
    setIsDirty(false);
    setIsReordering(false);
  };

  const handleDeleteClick = (e: React.MouseEvent, model: LLMModelSpecification) => {
    e.stopPropagation();
    setModelToDelete(model);
    showDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!modelToDelete) {
      return;
    }

    await remove.mutateAsync({ provider: modelToDelete.provider, name: modelToDelete.name });
    setModelToDelete(null);
  };

  const handleDeleteCancel = () => {
    setModelToDelete(null);
  };

  if (isLoading) {
    return (
      <Box className="p-4">
        <Loader />
      </Box>
    );
  }

  if (!sortedModelSpecifications || sortedModelSpecifications.length === 0) {
    return <Box className="p-4 text-sm text-gray-500">No model specifications available for this provider.</Box>;
  }

  return (
    <Box className="flex flex-col gap-4">
      <ModeActions
        isReordering={isReordering}
        isDirty={isDirty}
        isSaving={reorder.isPending}
        onStartReorder={handleStartReorder}
        onCancelReorder={handleCancelReorder}
        onSaveReorder={() => void handleSaveReorder()}
      />
      {isReordering ? (
        <ReorderModeContent groups={displayedGroups} onDragEnd={handleDragEnd} />
      ) : (
        <DisplayModeContent
          groups={grouped}
          isDeleting={remove.isPending}
          onDelete={handleDeleteClick}
          onEdit={onEdit}
        />
      )}
      <DeleteDialog
        title="Delete Model Specification"
        onConfirm={() => void handleDeleteConfirm()}
        onCancel={handleDeleteCancel}
        buttonsSettings={{ confirmText: 'Delete', confirmColor: 'error' }}
      >
        <Typography>
          Are you sure you want to delete the model specification <strong>{modelToDelete?.name}</strong>?
        </Typography>
      </DeleteDialog>
    </Box>
  );
};

export default ModelSpecificationsView;
